import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Check, Clock, Layers, Mic, Plus, Sparkles, Trash2, X } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    AiPromptCluster,
    AiPromptGroup,
    AiPromptLog,
    AiPromptQuery,
    AiPromptStats,
    AiPromptUserStat,
    AiSuggestionRow,
    createAiPromptCluster,
    createAiSuggestion,
    deleteAiPromptCluster,
    deleteAiSuggestion,
    fetchAiPromptClusters,
    fetchAiPromptGroups,
    fetchAiPromptLogs,
    fetchAiPromptStats,
    fetchAiSuggestionRows,
    reorderAiSuggestions,
    seedAiSuggestions,
    updateAiPromptCluster,
    updateAiSuggestion,
} from "../../../interfaces/aiPrompts";

type Tab = "prompts" | "clusters" | "calls" | "suggestions";

const PERIODS: { label: string; value: AiPromptQuery["days"] }[] = [
    { label: "7 j", value: 7 },
    { label: "30 j", value: 30 },
    { label: "90 j", value: 90 },
    { label: "Tout", value: "all" },
];

function formatDate(value?: string | null) {
    if (!value) return "—";
    const date = new Date(value);
    const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffMins < 1440) return `Il y a ${Math.floor(diffMins / 60)}h`;
    if (diffMins < 10080) return `Il y a ${Math.floor(diffMins / 1440)}j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "2-digit" });
}

function StatusPill({ status }: { status: "ok" | "empty" | "error" }) {
    const styles = {
        ok: "bg-green-50 text-green-700 border-green-100",
        empty: "bg-amber-50 text-amber-700 border-amber-100",
        error: "bg-red-50 text-red-700 border-red-100",
    }[status];
    const label = { ok: "Filtré", empty: "Vide", error: "Erreur" }[status];
    return <span className={`px-2 py-0.5 rounded text-[11px] font-medium border ${styles}`}>{label}</span>;
}

function SourceIcon({ source }: { source: "manual" | "suggestion" | "voice" }) {
    if (source === "voice") return <Mic className="w-3.5 h-3.5 text-gray-400" aria-label="Dicté" />;
    if (source === "suggestion") return <Sparkles className="w-3.5 h-3.5 text-gray-400" aria-label="Suggestion" />;
    return <span className="text-[11px] text-gray-400">Saisi</span>;
}

/**
 * Every call to the AI bar, the distinct wordings behind them, and the clusters an admin
 * fuses those wordings into.
 *
 * The clustering is deliberately manual: what counts as "the same kind of search" is an
 * editorial call, so the page's job is to make the raw material easy to read and to fuse,
 * not to guess the grouping. A cluster holds prompt texts, so once fused it keeps counting
 * new calls of the same wording on its own.
 *
 * A prompt can sit in several clusters at once — "les boulangeries à Metz" belongs to
 * "recherche par ville" as much as to "boulangerie" — so "Ajouter" files it somewhere new
 * without taking it out of where it already is. Leaving a cluster is done from that cluster.
 */
export default function AiPrompts() {
    const [tab, setTab] = useState<Tab>("prompts");
    const [days, setDays] = useState<AiPromptQuery["days"]>(30);
    const [context, setContext] = useState<"all" | "database" | "signals">("all");
    const [status, setStatus] = useState<"all" | "ok" | "empty" | "error">("all");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [email, setEmail] = useState("");

    const [stats, setStats] = useState<AiPromptStats | null>(null);
    const [byUser, setByUser] = useState<AiPromptUserStat[]>([]);
    const [groups, setGroups] = useState<AiPromptGroup[]>([]);
    const [clusters, setClusters] = useState<AiPromptCluster[]>([]);
    const [logs, setLogs] = useState<AiPromptLog[]>([]);
    const [logTotal, setLogTotal] = useState(0);
    const [logPage, setLogPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [selected, setSelected] = useState<string[]>([]);
    const [clusterName, setClusterName] = useState("");
    const [targetCluster, setTargetCluster] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
        return () => clearTimeout(timer);
    }, [search]);

    const query = useMemo<AiPromptQuery>(
        () => ({ days, context, status, q: debouncedSearch || undefined, email: email || undefined }),
        [days, context, status, debouncedSearch, email]
    );

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, groupsRes, clustersRes] = await Promise.all([
                fetchAiPromptStats(query),
                fetchAiPromptGroups(query),
                fetchAiPromptClusters(query),
            ]);
            setStats(statsRes.stats);
            setByUser(statsRes.byUser);
            setGroups(groupsRes);
            setClusters(clustersRes);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [query]);

    useEffect(() => {
        load();
    }, [load]);

    // The raw log is paginated on its own, and only fetched while its tab is open.
    useEffect(() => {
        if (tab !== "calls") return;
        let cancelled = false;
        fetchAiPromptLogs(query, logPage, 50)
            .then(res => {
                if (cancelled) return;
                setLogs(res.logs);
                setLogTotal(res.total);
            })
            .catch(console.error);
        return () => {
            cancelled = true;
        };
    }, [tab, query, logPage]);

    useEffect(() => setLogPage(1), [query]);

    const toggleSelect = (promptKey: string) =>
        setSelected(prev => (prev.includes(promptKey) ? prev.filter(k => k !== promptKey) : [...prev, promptKey]));

    // The API normalises whatever wording it is handed, so the raw prompts are sent as-is.
    const selectedPrompts = useMemo(
        () => groups.filter(g => selected.includes(g.promptKey)).map(g => g.prompt),
        [groups, selected]
    );

    const selectedCalls = useMemo(
        () => groups.filter(g => selected.includes(g.promptKey)).reduce((sum, g) => sum + g.calls, 0),
        [groups, selected]
    );

    const runFuse = async (action: () => Promise<unknown>) => {
        setBusy(true);
        try {
            await action();
            setSelected([]);
            setClusterName("");
            setTargetCluster("");
            await load();
        } catch (err) {
            console.error(err);
            window.alert("L'opération a échoué.");
        } finally {
            setBusy(false);
        }
    };

    const maxCalls = groups[0]?.calls || 1;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">Prompts IA</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Toutes les recherches passées par la barre DuckFlow AI, et les clusters que vous en faites
                    </p>
                </div>

                <BONavigation />

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    {[
                        { label: "Appels", value: stats?.calls ?? 0 },
                        { label: "Prompts uniques", value: stats?.uniquePrompts ?? 0 },
                        { label: "Utilisateurs", value: stats?.users ?? 0 },
                        { label: "Sans résultat", value: (stats?.empty ?? 0) + (stats?.errors ?? 0) },
                        { label: "Dictés", value: stats?.voice ?? 0 },
                    ].map(card => (
                        <div key={card.label} className="bg-white border border-gray-200 rounded-xl p-4">
                            <p className="text-xs text-gray-500 mb-1">{card.label}</p>
                            <p className="text-2xl font-semibold text-gray-900">{card.value.toLocaleString()}</p>
                        </div>
                    ))}
                </div>

                {/* Filters — they scope the call history, so the suggestions editor hides them */}
                <div
                    className={`bg-white border border-gray-200 rounded-xl p-4 mb-4 flex-wrap items-center gap-3 ${
                        tab === "suggestions" ? "hidden" : "flex"
                    }`}
                >
                    <div className="flex gap-1">
                        {PERIODS.map(p => (
                            <button
                                key={String(p.value)}
                                onClick={() => setDays(p.value)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    days === p.value ? "bg-[#1A1A1A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1">
                        {(["all", "database", "signals"] as const).map(c => (
                            <button
                                key={c}
                                onClick={() => setContext(c)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    context === c ? "bg-[#1A1A1A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                            >
                                {c === "all" ? "Toutes pages" : c === "database" ? "Base" : "Signaux"}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1">
                        {(["all", "ok", "empty", "error"] as const).map(s => (
                            <button
                                key={s}
                                onClick={() => setStatus(s)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    status === s ? "bg-[#1A1A1A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                            >
                                {{ all: "Tous", ok: "Filtrés", empty: "Vides", error: "Erreurs" }[s]}
                            </button>
                        ))}
                    </div>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Chercher dans les prompts…"
                        className="ml-auto w-64 border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-gray-400"
                    />
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-4">
                    {([
                        ["prompts", `Prompts (${groups.length})`],
                        ["clusters", `Clusters (${clusters.length})`],
                        ["calls", "Appels"],
                        ["suggestions", "Suggestions"],
                    ] as [Tab, string][]).map(([id, label]) => (
                        <button
                            key={id}
                            onClick={() => setTab(id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                tab === id ? "bg-white border border-gray-200 text-gray-900" : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {/* Fusing bar — only while something is selected */}
                {tab === "prompts" && selected.length > 0 && (
                    <div className="bg-white border border-gray-900 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
                        <span className="text-xs font-medium text-gray-900">
                            {selected.length} prompt{selected.length > 1 ? "s" : ""} · {selectedCalls} appel
                            {selectedCalls > 1 ? "s" : ""}
                        </span>
                        <button onClick={() => setSelected([])} className="text-xs text-gray-500 hover:text-gray-800">
                            Désélectionner
                        </button>

                        <div className="flex items-center gap-2 ml-auto">
                            <input
                                value={clusterName}
                                onChange={e => setClusterName(e.target.value)}
                                placeholder="Nom du nouveau cluster"
                                className="w-56 border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-gray-400"
                            />
                            <button
                                disabled={busy || !clusterName.trim()}
                                onClick={() => runFuse(() => createAiPromptCluster(clusterName.trim(), selectedPrompts))}
                                className="px-3 py-1.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium disabled:opacity-40"
                            >
                                Fusionner
                            </button>
                        </div>

                        {clusters.length > 0 && (
                            <div className="flex items-center gap-2">
                                <select
                                    value={targetCluster}
                                    onChange={e => setTargetCluster(e.target.value)}
                                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-gray-400"
                                >
                                    <option value="">…ou ajouter à un cluster</option>
                                    {clusters.map(c => (
                                        <option key={c._id} value={c._id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    disabled={busy || !targetCluster}
                                    onClick={() =>
                                        runFuse(() => updateAiPromptCluster(targetCluster, { addPrompts: selectedPrompts }))
                                    }
                                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 text-xs font-medium disabled:opacity-40"
                                >
                                    Ajouter
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {tab === "suggestions" ? (
                    <SuggestionsEditor />
                ) : loading ? (
                    <div className="bg-white border border-gray-200 rounded-xl py-16 text-center text-sm text-gray-400">
                        Chargement…
                    </div>
                ) : tab === "prompts" ? (
                    <PromptsTable
                        groups={groups}
                        selected={selected}
                        onToggle={toggleSelect}
                        onSelectAll={() =>
                            setSelected(selected.length === groups.length ? [] : groups.map(g => g.promptKey))
                        }
                        maxCalls={maxCalls}
                    />
                ) : tab === "clusters" ? (
                    <ClustersView
                        clusters={clusters}
                        busy={busy}
                        onRename={(id, name) => runFuse(() => updateAiPromptCluster(id, { name }))}
                        onRemovePrompt={(id, sample) => runFuse(() => updateAiPromptCluster(id, { removePrompts: [sample] }))}
                        onDelete={id => runFuse(() => deleteAiPromptCluster(id))}
                    />
                ) : (
                    <CallsTable
                        logs={logs}
                        total={logTotal}
                        page={logPage}
                        onPage={setLogPage}
                    />
                )}

                {/* Per-user usage — the same window as everything above */}
                {tab === "prompts" && byUser.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-4">
                        <p className="text-xs font-medium text-gray-500 mb-3">
                            Utilisation par compte {email && <span className="text-gray-400">· filtré sur {email}</span>}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {byUser.map(u => (
                                <button
                                    key={u.email}
                                    onClick={() => setEmail(email === u.email ? "" : u.email)}
                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border transition-colors ${
                                        email === u.email
                                            ? "border-gray-900 bg-white"
                                            : "border-gray-200 bg-gray-50 hover:border-gray-300"
                                    }`}
                                >
                                    <span className="text-xs text-gray-700">{u.email || "—"}</span>
                                    <span className="text-[11px] font-medium text-gray-900">{u.calls}</span>
                                    <span className="text-[11px] text-gray-400">{u.uniquePrompts} uniques</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function PromptsTable({
    groups,
    selected,
    onToggle,
    onSelectAll,
    maxCalls,
}: {
    groups: AiPromptGroup[];
    selected: string[];
    onToggle: (key: string) => void;
    onSelectAll: () => void;
    maxCalls: number;
}) {
    const [addedKeys, setAddedKeys] = useState<string[]>([]);
    const [busyKey, setBusyKey] = useState<string | null>(null);

    // A real, already-typed-by-users wording, promoted as-is into the curated openers — the
    // prompt must stay exactly what was typed, which is the point of picking it from here
    // rather than writing one from scratch.
    const addToSuggestions = async (g: AiPromptGroup) => {
        setBusyKey(g.promptKey);
        try {
            const context = g.contexts.length === 1 ? g.contexts[0] : "both";
            await createAiSuggestion({ prompt: g.prompt, context });
            setAddedKeys(prev => [...prev, g.promptKey]);
        } catch (err) {
            console.error(err);
            window.alert("L'ajout aux suggestions a échoué.");
        } finally {
            setBusyKey(null);
        }
    };

    if (groups.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl py-16 text-center text-sm text-gray-400">
                Aucun prompt sur cette période
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
                <thead>
                    <tr className="border-b border-gray-100">
                        <th className="px-4 py-3 w-8">
                            <input
                                type="checkbox"
                                checked={selected.length === groups.length && groups.length > 0}
                                onChange={onSelectAll}
                                className="rounded border-gray-300"
                            />
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Prompt</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-40">Clusters</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-36">Appels</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-28">Résultat</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-28">Dernier</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-32">Suggestion</th>
                    </tr>
                </thead>
                <tbody>
                    {groups.map(g => (
                        <tr
                            key={g.promptKey}
                            className={`border-b border-gray-50 hover:bg-[#F9F6F3] ${
                                selected.includes(g.promptKey) ? "bg-[#F5F0EB]" : ""
                            }`}
                        >
                            <td className="px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={selected.includes(g.promptKey)}
                                    onChange={() => onToggle(g.promptKey)}
                                    className="rounded border-gray-300"
                                />
                            </td>
                            <td className="px-4 py-3">
                                <p className="text-xs text-gray-900">{g.prompt}</p>
                                <p className="text-[11px] text-gray-400 mt-0.5">
                                    {g.users.join(" · ") || "—"} · {g.avgFilters} filtre{g.avgFilters > 1 ? "s" : ""} en moyenne
                                </p>
                            </td>
                            <td className="px-4 py-3">
                                {g.clusters.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                        {g.clusters.map(c => (
                                            <span
                                                key={c._id}
                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                            >
                                                <Layers className="w-3 h-3" />
                                                {c.name}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="text-[11px] text-gray-300">—</span>
                                )}
                            </td>
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-[#1A1A1A] rounded-full"
                                            style={{ width: `${(g.calls / maxCalls) * 100}%` }}
                                        />
                                    </div>
                                    <span className="text-xs font-medium text-gray-900 w-8 text-right">{g.calls}x</span>
                                </div>
                            </td>
                            <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1">
                                    {g.okCalls > 0 && <StatusPill status="ok" />}
                                    {g.emptyCalls > 0 && <StatusPill status="empty" />}
                                    {g.errorCalls > 0 && <StatusPill status="error" />}
                                </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">{formatDate(g.lastUsedAt)}</td>
                            <td className="px-4 py-3">
                                {addedKeys.includes(g.promptKey) ? (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-green-700">
                                        <Check className="w-3.5 h-3.5" />
                                        Ajouté
                                    </span>
                                ) : (
                                    <button
                                        disabled={busyKey === g.promptKey}
                                        onClick={() => addToSuggestions(g)}
                                        className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-900 disabled:opacity-40"
                                    >
                                        <Sparkles className="w-3.5 h-3.5" />
                                        {busyKey === g.promptKey ? "…" : "Suggérer"}
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ClustersView({
    clusters,
    busy,
    onRename,
    onRemovePrompt,
    onDelete,
}: {
    clusters: AiPromptCluster[];
    busy: boolean;
    onRename: (id: string, name: string) => void;
    onRemovePrompt: (id: string, sample: string) => void;
    onDelete: (id: string) => void;
}) {
    if (clusters.length === 0) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl py-16 text-center text-sm text-gray-400">
                Aucun cluster. Sélectionnez des prompts dans l'onglet précédent pour en créer un.
            </div>
        );
    }

    const maxCalls = Math.max(...clusters.map(c => c.calls), 1);

    return (
        <div className="space-y-3">
            {clusters.map(cluster => (
                <div key={cluster._id} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                        <Layers className="w-4 h-4 text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-900">{cluster.name}</h3>
                        <span className="text-xs text-gray-500">
                            {cluster.calls} appel{cluster.calls > 1 ? "s" : ""} · {cluster.prompts.length} prompt
                            {cluster.prompts.length > 1 ? "s" : ""} · {cluster.users} utilisateur
                            {cluster.users > 1 ? "s" : ""}
                        </span>
                        <div className="flex items-center gap-1 text-xs text-gray-400 ml-auto">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDate(cluster.lastUsedAt)}
                        </div>
                        <button
                            disabled={busy}
                            onClick={() => {
                                const name = window.prompt("Nouveau nom du cluster", cluster.name);
                                if (name?.trim()) onRename(cluster._id, name.trim());
                            }}
                            className="text-xs text-gray-500 hover:text-gray-900"
                        >
                            Renommer
                        </button>
                        <button
                            disabled={busy}
                            onClick={() => {
                                if (window.confirm(`Supprimer le cluster "${cluster.name}" ? Les appels sont conservés.`))
                                    onDelete(cluster._id);
                            }}
                            className="text-gray-400 hover:text-red-600"
                            aria-label="Supprimer le cluster"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
                        <div className="h-full bg-[#1A1A1A] rounded-full" style={{ width: `${(cluster.calls / maxCalls) * 100}%` }} />
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                        {cluster.prompts.map(p => (
                            <span
                                key={p.promptKey}
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-gray-200 bg-gray-50 text-[11px] text-gray-700"
                            >
                                {p.sample}
                                <span className="font-medium text-gray-900">{p.calls}x</span>
                                <button
                                    disabled={busy}
                                    onClick={() => onRemovePrompt(cluster._id, p.sample)}
                                    className="text-gray-400 hover:text-red-600"
                                    aria-label="Retirer du cluster"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

const CONTEXT_LABEL: Record<AiSuggestionRow["context"], string> = {
    both: "Les deux",
    database: "Base",
    signals: "Signaux",
};

/**
 * Editor for the openers the AI bar shows when a user clicks it.
 *
 * Text edits save on blur rather than on every keystroke — an admin retypes a whole wording,
 * and one PATCH per character would be both noisy and racy. Order is what the bar sorts by,
 * so the arrows persist the whole list at once.
 */
function SuggestionsEditor() {
    const [rows, setRows] = useState<AiSuggestionRow[]>([]);
    const [seeded, setSeeded] = useState(true);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState<{ prompt: string; context: AiSuggestionRow["context"] }>({
        prompt: "",
        context: "both",
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchAiSuggestionRows();
            setRows(res.suggestions);
            setSeeded(res.seeded);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        try {
            await action();
            await load();
        } catch (err) {
            console.error(err);
            window.alert("L'opération a échoué.");
        } finally {
            setBusy(false);
        }
    };

    /** Local edit only — persisted by `save` when the field loses focus. */
    const edit = (id: string, patch: Partial<AiSuggestionRow>) =>
        setRows(prev => prev.map(r => (r._id === id ? { ...r, ...patch } : r)));

    /** What a text field held when it gained focus, so a blur that changed nothing sends nothing. */
    const focusValue = useRef("");

    const save = (row: AiSuggestionRow, patch: Partial<AiSuggestionRow>) => {
        // An emptied field is not a value to persist — and leaving the blank on screen would
        // read as saved, so the stored row is put back instead.
        if (!row.prompt.trim()) {
            load();
            return;
        }
        updateAiSuggestion(row._id, patch).catch(err => {
            console.error(err);
            load();
        });
    };

    const move = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= rows.length) return;
        const next = [...rows];
        [next[index], next[target]] = [next[target], next[index]];
        setRows(next);
        reorderAiSuggestions(next.map(r => r._id)).catch(err => {
            console.error(err);
            load();
        });
    };

    const add = () => {
        if (!draft.prompt.trim()) return;
        run(async () => {
            await createAiSuggestion(draft);
            setDraft({ prompt: "", context: "both" });
        });
    };

    if (loading) {
        return (
            <div className="bg-white border border-gray-200 rounded-xl py-16 text-center text-sm text-gray-400">
                Chargement…
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {!seeded && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                    <p className="text-xs text-amber-800">
                        Aucune suggestion enregistrée : la barre affiche encore la liste intégrée par défaut. Importez-la
                        pour pouvoir la modifier.
                    </p>
                    <button
                        disabled={busy}
                        onClick={() => run(seedAiSuggestions)}
                        className="ml-auto px-3 py-1.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium disabled:opacity-40"
                    >
                        Importer les suggestions par défaut
                    </button>
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-500">
                        Proposées sous la barre DuckFlow AI, dans cet ordre. Un clic lance la recherche.
                    </p>
                    {seeded && (
                        <button
                            disabled={busy}
                            onClick={() => run(seedAiSuggestions)}
                            className="ml-auto text-xs text-gray-500 hover:text-gray-900 disabled:opacity-40"
                        >
                            Réimporter celles par défaut
                        </button>
                    )}
                </div>

                {rows.length === 0 ? (
                    <div className="py-12 text-center text-sm text-gray-400">
                        Aucune suggestion. Ajoutez-en une ci-dessous.
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-100">
                                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 w-16">Ordre</th>
                                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Prompt lancé</th>
                                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 w-28">Page</th>
                                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 w-20">Actif</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={row._id} className={`border-b border-gray-50 ${row.active ? "" : "opacity-50"}`}>
                                    <td className="px-4 py-2">
                                        <div className="flex gap-0.5">
                                            <button
                                                disabled={index === 0}
                                                onClick={() => move(index, -1)}
                                                className="p-1 text-gray-400 hover:text-gray-900 disabled:opacity-20"
                                                aria-label="Monter"
                                            >
                                                <ArrowUp className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                disabled={index === rows.length - 1}
                                                onClick={() => move(index, 1)}
                                                className="p-1 text-gray-400 hover:text-gray-900 disabled:opacity-20"
                                                aria-label="Descendre"
                                            >
                                                <ArrowDown className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2">
                                        <input
                                            value={row.prompt}
                                            onChange={e => edit(row._id, { prompt: e.target.value })}
                                            onFocus={e => { focusValue.current = e.target.value; }}
                                            onBlur={() => {
                                                if (row.prompt !== focusValue.current) save(row, { prompt: row.prompt });
                                            }}
                                            className="w-full border border-transparent hover:border-gray-200 focus:border-gray-400 rounded px-2 py-1 text-xs focus:outline-none"
                                        />
                                    </td>
                                    <td className="px-4 py-2">
                                        <select
                                            value={row.context}
                                            onChange={e => {
                                                const context = e.target.value as AiSuggestionRow["context"];
                                                edit(row._id, { context });
                                                save({ ...row, context }, { context });
                                            }}
                                            className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-gray-400"
                                        >
                                            {(["both", "database", "signals"] as const).map(c => (
                                                <option key={c} value={c}>
                                                    {CONTEXT_LABEL[c]}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-2">
                                        <label className="flex items-center gap-2 text-xs text-gray-600">
                                            <input
                                                type="checkbox"
                                                checked={row.active}
                                                onChange={e => {
                                                    const active = e.target.checked;
                                                    edit(row._id, { active });
                                                    save({ ...row, active }, { active });
                                                }}
                                                className="rounded border-gray-300"
                                            />
                                            {row.active ? "Oui" : "Non"}
                                        </label>
                                    </td>
                                    <td className="px-4 py-2">
                                        <button
                                            disabled={busy}
                                            onClick={() => {
                                                if (window.confirm(`Supprimer la suggestion "${row.prompt}" ?`))
                                                    run(() => deleteAiSuggestion(row._id));
                                            }}
                                            className="text-gray-400 hover:text-red-600"
                                            aria-label="Supprimer"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* New suggestion */}
                <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50">
                    <input
                        value={draft.prompt}
                        onChange={e => setDraft({ ...draft, prompt: e.target.value })}
                        onKeyDown={e => {
                            if (e.key === "Enter") add();
                        }}
                        placeholder="Prompt lancé au clic, ex. : Les boulangeries en Île-de-France"
                        className="flex-1 min-w-[16rem] border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-gray-400"
                    />
                    <select
                        value={draft.context}
                        onChange={e => setDraft({ ...draft, context: e.target.value as AiSuggestionRow["context"] })}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-gray-400"
                    >
                        {(["both", "database", "signals"] as const).map(c => (
                            <option key={c} value={c}>
                                {CONTEXT_LABEL[c]}
                            </option>
                        ))}
                    </select>
                    <button
                        disabled={busy || !draft.prompt.trim()}
                        onClick={add}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium disabled:opacity-40"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Ajouter
                    </button>
                </div>
            </div>

            {/* Preview — the same chips the bar renders, per page */}
            {rows.some(r => r.active) && (
                <div className="grid md:grid-cols-2 gap-4">
                    {(["database", "signals"] as const).map(context => (
                        <div key={context} className="bg-white border border-gray-200 rounded-xl p-4">
                            <p className="text-xs font-medium text-gray-500 mb-3">
                                Aperçu · page {context === "database" ? "Base" : "Signaux"}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {rows
                                    .filter(r => r.active && (r.context === context || r.context === "both"))
                                    .map(r => (
                                        <span
                                            key={r._id}
                                            className="flex items-center px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50"
                                        >
                                            <span className="text-xs text-gray-900">{r.prompt}</span>
                                        </span>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function CallsTable({
    logs,
    total,
    page,
    onPage,
}: {
    logs: AiPromptLog[];
    total: number;
    page: number;
    onPage: (page: number) => void;
}) {
    const pages = Math.max(1, Math.ceil(total / 50));

    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {logs.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-400">Aucun appel sur cette période</div>
            ) : (
                <>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-100">
                                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-32">Quand</th>
                                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-52">Utilisateur</th>
                                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Prompt</th>
                                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-20">Page</th>
                                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-16">Source</th>
                                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-24">Résultat</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(log => (
                                <tr key={log._id} className="border-b border-gray-50 hover:bg-[#F9F6F3] align-top">
                                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(log.createdAt)}</td>
                                    <td className="px-4 py-3">
                                        <p className="text-xs text-gray-900">{log.userEmail || "—"}</p>
                                        {log.userName && <p className="text-[11px] text-gray-400">{log.userName}</p>}
                                    </td>
                                    <td className="px-4 py-3">
                                        <p className="text-xs text-gray-900">{log.prompt}</p>
                                        {log.filterKeys.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {log.filterKeys.map(key => (
                                                    <span
                                                        key={key}
                                                        className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-[10px] text-gray-600"
                                                    >
                                                        {key}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {log.unsupported.length > 0 && (
                                            <p className="text-[11px] text-amber-700 mt-1">
                                                Non couvert : {log.unsupported.join(" · ")}
                                            </p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-500">
                                        {log.context === "database" ? "Base" : "Signaux"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <SourceIcon source={log.source} />
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusPill status={log.status} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                        <span className="text-xs text-gray-500">
                            {total.toLocaleString()} appel{total > 1 ? "s" : ""} · page {page} / {pages}
                        </span>
                        <div className="flex gap-2">
                            <button
                                disabled={page <= 1}
                                onClick={() => onPage(page - 1)}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 disabled:opacity-40"
                            >
                                Précédent
                            </button>
                            <button
                                disabled={page >= pages}
                                onClick={() => onPage(page + 1)}
                                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 disabled:opacity-40"
                            >
                                Suivant
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
